import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast, Toaster } from "sonner";
import {
  CalendarClock, CalendarDays, DoorOpen, Users, Stethoscope, Settings,
  ArrowUpRight, ArrowRight, Menu, X, Loader2, CheckCircle2, Mail,
  Cpu, LineChart, ClipboardCheck, HeartPulse, ShieldCheck, Sparkles, Server,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SYMBOL = "/brand/clinova-symbol.png";

const up = {
  hidden: { opacity: 0, y: 22 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};
const ring = "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--clv-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--clv-paper)]";

const NAV = [
  { label: "Platform", href: "#platform" },
  { label: "Capacity & Demand", href: "#capacity" },
  { label: "How it works", href: "#engine" },
  { label: "Security", href: "#security" },
  { label: "About", href: "#about" },
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const s = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", s); return () => window.removeEventListener("scroll", s);
  }, []);
  return (
    <header data-testid="nav-header" className={`fixed top-0 inset-x-0 z-50 transition-all ${scrolled ? "bg-[#f6f1e6]/85 backdrop-blur-xl border-b border-[var(--clv-border)]" : "bg-transparent"}`}>
      <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className={`flex items-center gap-2 rounded-md ${ring}`}>
          <img src={SYMBOL} alt="" className="h-7 w-7 object-contain" />
          <span className="font-display text-lg font-semibold text-[var(--clv-forest)]">Clinova<span style={{ color: "var(--clv-primary)" }}>360</span></span>
        </a>
        <nav aria-label="Primary" className="hidden md:flex items-center gap-7">
          {NAV.map((l) => <a key={l.href} href={l.href} data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g,"-")}`} className={`text-sm font-medium text-[var(--clv-text-soft)] hover:text-[var(--clv-forest)] rounded-sm ${ring}`}>{l.label}</a>)}
        </nav>
        <a href="#contact" data-testid="nav-cta" className={`hidden md:inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium text-white ${ring}`} style={{ background: "var(--clv-primary)" }}>Request a demo</a>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(v=>!v)} className={`md:hidden text-[var(--clv-forest)] ${ring}`} aria-label="Menu" aria-expanded={open}>{open ? <X size={22}/> : <Menu size={22}/>}</button>
      </div>
      {open && <div data-testid="mobile-menu" className="md:hidden bg-[#f6f1e6]/97 backdrop-blur-xl border-b border-[var(--clv-border)] px-6 py-5 flex flex-col gap-3">
        {NAV.map((l)=><a key={l.href} href={l.href} onClick={()=>setOpen(false)} className="text-[var(--clv-forest)] font-medium">{l.label}</a>)}
        <a href="#contact" onClick={()=>setOpen(false)} className="mt-1 rounded-full px-4 py-2.5 text-center text-white font-medium" style={{background:"var(--clv-primary)"}}>Request a demo</a>
      </div>}
    </header>
  );
};

const Reveal = ({ children, i = 0, className = "" }) => (
  <motion.div custom={i} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} variants={up} className={className}>{children}</motion.div>
);

const Metric = ({ k, v, red }) => (
  <div className={`rounded-2xl border p-4 ${red ? "bg-[#fbeeee] border-[#eec9c9]" : "bg-white border-[var(--clv-border)]"}`}>
    <div className="text-xs font-semibold text-[var(--clv-text-soft)]">{k}</div>
    <div className={`font-display text-3xl font-bold mt-1 ${red ? "text-[#c0453f]" : "text-[var(--clv-forest)]"}`}>{v}</div>
  </div>
);

const ProductCard = () => (
  <div className="mx-auto max-w-3xl rounded-3xl border border-[var(--clv-border)] bg-[var(--clv-card)] overflow-hidden" style={{ boxShadow: "0 40px 90px -46px rgba(26,51,34,.45)" }}>
    <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[var(--clv-border)] bg-[var(--clv-paper-2)]">
      <span className="h-3 w-3 rounded-full bg-[#e0b3ac]" /><span className="h-3 w-3 rounded-full bg-[#e6cf9b]" /><span className="h-3 w-3 rounded-full bg-[#a9c3a0]" />
      <span className="ml-2 text-[13px] font-semibold text-[var(--clv-forest)]">Future Rota · Capacity — Thursday 24 September</span>
    </div>
    <div className="p-6 grid sm:grid-cols-[1.3fr_1fr] gap-5">
      <div>
        <div className="grid grid-cols-3 gap-3">
          <Metric k="Target" v="184" /><Metric k="Planned" v="168" /><Metric k="Shortfall" v="−16" red />
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-xs font-semibold text-[var(--clv-forest)] mb-1.5"><span>Planned vs target · appointments</span><span>91%</span></div>
          <div className="h-3 rounded-full bg-[#e7e0cf] overflow-hidden"><div className="h-full rounded-full" style={{ width: "91%", background: "linear-gradient(90deg,var(--clv-primary),var(--clv-sage-deep))" }} /></div>
        </div>
      </div>
      <div className="sm:border-l border-[var(--clv-border)] sm:pl-5 flex flex-col gap-2.5">
        {[["#c99a12","2 clinicians on leave"],["var(--clv-primary)","Rooms 7 / 9 in use"],["#4f8a55","1 registrar · supervised"]].map(([c,t])=>(
          <div key={t} className="flex items-start gap-2.5 text-[13px] text-[var(--clv-forest)] bg-[var(--clv-paper-2)] border border-[var(--clv-border)] rounded-xl px-3 py-2.5"><span className="h-2 w-2 rounded-full mt-1.5" style={{background:c}}/>{t}</div>
        ))}
        <div className="flex items-start gap-2.5 text-[13px] font-semibold rounded-xl px-3 py-2.5" style={{background:"rgba(94,115,91,.12)",color:"var(--clv-primary-hover)"}}><Sparkles size={15} className="mt-0.5"/>Suggested action — review session movement</div>
      </div>
    </div>
  </div>
);

const Hero = () => (
  <section id="top" className="relative pt-24 pb-4 text-center overflow-hidden">
    <div className="absolute inset-0 clv-grain opacity-50" aria-hidden="true" />
    <div className="relative max-w-4xl mx-auto px-6">
      <motion.p initial="hidden" animate="show" variants={up} className="clv-eyebrow mb-4">Operational intelligence for General Practice</motion.p>
      <motion.h1 custom={1} initial="hidden" animate="show" variants={up} className="font-display text-4xl sm:text-6xl font-light tracking-tight text-[var(--clv-forest)] leading-[1.03]">
        Sorry, one size does <span className="font-semibold" style={{ color: "var(--clv-forest)" }}>NOT</span> fit all.<br />
        <span className="italic font-medium" style={{ color: "var(--clv-primary)" }}>Your practice has its own rules.</span>
        <span className="flex items-center justify-center gap-3 mt-2">
          <img src={SYMBOL} alt="" className="h-10 sm:h-14 w-auto" />
          <span className="italic font-medium" style={{ color: "var(--clv-primary)" }}><span style={{ color: "#0067ac" }}>Clinova</span>360<span className="not-italic font-bold" style={{ color: "#0a0a0a" }}> works with them!</span></span>
        </span>
      </motion.h1>
      <motion.p custom={2} initial="hidden" animate="show" variants={up} className="mt-6 text-lg leading-relaxed text-[var(--clv-text-soft)] max-w-2xl mx-auto">
        Model your workforce, leave, rooms, capacity and operational rules. A deterministic engine builds rotas, forecasts practice capacity, suggested leave and shows you exactly where action is needed. Innovative modules such as induction tutorials. Engine analysis of CQC readiness, QOF progress, Google Reviews, and Practice Health — keeping you informed. Always.
      </motion.p>
      <motion.div custom={3} initial="hidden" animate="show" variants={up} className="mt-8 flex items-center justify-center gap-5 flex-wrap">
        <a href="#contact" data-testid="hero-primary-cta" className={`inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-medium text-white shadow-sm hover:shadow-lg transition-all ${ring}`} style={{ background: "var(--clv-primary)" }}>Request a demo <ArrowUpRight size={17}/></a>
        <a href="#engine" data-testid="hero-secondary-cta" className={`inline-flex items-center gap-1.5 text-sm font-semibold rounded-sm ${ring}`} style={{ color: "var(--clv-primary-hover)" }}>See how it works →</a>
      </motion.div>
      <motion.div custom={4} initial="hidden" animate="show" variants={up} className="mt-7 flex justify-center gap-2.5 flex-wrap">
        {[
          { label: "Designed by a GP", Icon: Stethoscope, color: "#4f8a55", bg: "rgba(79,138,85,.14)" },
          { label: "NHS Aligned", nhs: true },
          { label: "UK hosted", Icon: Server, color: "#2a6ebb", bg: "rgba(42,110,187,.12)" },
          { label: "Built for UK GDPR", Icon: ShieldCheck, color: "#2f9c8f", bg: "rgba(47,156,143,.14)" },
        ].map(({ label, Icon, nhs, color, bg }) => (
          <span key={label} data-testid={`trust-badge-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="inline-flex items-center gap-2 text-[13px] font-medium text-[var(--clv-forest)] bg-[var(--clv-card)] border border-[var(--clv-border)] rounded-full pl-1.5 pr-4 py-1.5">
            {nhs
              ? <span className="inline-flex items-center justify-center rounded-md bg-[#005eb8] text-white font-bold text-[9px] tracking-tight px-1.5 h-6">NHS</span>
              : <span className="inline-flex items-center justify-center h-6 w-6 rounded-full" style={{ background: bg }}><Icon size={14} style={{ color }} /></span>}
            {label}
          </span>
        ))}
      </motion.div>
    </div>
  </section>
);

const Connected = () => (
  <section className="py-24 sm:py-28"><div className="max-w-5xl mx-auto px-6">
    <Reveal className="text-center max-w-2xl mx-auto">
      <p className="clv-eyebrow mb-3">The connected problem</p>
      <h2 className="font-display text-3xl sm:text-4xl font-medium text-[var(--clv-forest)]">Everything in a practice is connected.</h2>
      <p className="mt-4 text-[var(--clv-text-soft)] text-lg">Running it shouldn't mean disconnected spreadsheets and manual cross-checking.</p>
    </Reveal>
    <Reveal className="mt-12 flex flex-wrap items-center justify-center gap-3">
      {["Rotas","Leave","Capacity","Appointments"].map((t,i,a)=>(
        <div key={t} className="flex items-center gap-3">
          <span className="rounded-full bg-[var(--clv-card)] border border-[var(--clv-border)] px-5 py-2.5 font-display font-medium text-[var(--clv-forest)]">{t}</span>
          {i<a.length-1 && <ArrowRight size={18} style={{color:"var(--clv-primary)"}}/>}
        </div>
      ))}
    </Reveal>
    <Reveal className="mt-4 flex flex-wrap items-center justify-center gap-3">
      <span className="rounded-full bg-[var(--clv-card)] border border-[var(--clv-border)] px-5 py-2.5 font-display font-medium text-[var(--clv-forest)]">Supervision</span>
      <ArrowRight size={18} style={{color:"var(--clv-primary)"}}/>
      <span className="rounded-full bg-[var(--clv-card)] border border-[var(--clv-border)] px-5 py-2.5 font-display font-medium text-[var(--clv-forest)]">Rooms & where clinicians work</span>
    </Reveal>
    <Reveal className="mt-8 text-center"><p className="font-display text-2xl font-medium text-[var(--clv-forest)]">Clinova360 connects them — <span style={{color:"var(--clv-primary)"}}>one operational model.</span></p></Reveal>
    <div className="relative mt-12"><Reveal><ProductCard/></Reveal></div>
  </div></section>
);

const Engine = () => (
  <section id="engine" className="py-24 sm:py-28 bg-[var(--clv-paper-2)]/60 border-y border-[var(--clv-border)]"><div className="max-w-6xl mx-auto px-6">
    <Reveal className="text-center max-w-2xl mx-auto">
      <p className="clv-eyebrow mb-3">The deterministic engine</p>
      <h2 className="font-display text-3xl sm:text-4xl font-medium text-[var(--clv-forest)]">Not a black box. A rules-based engine.</h2>
      <p className="mt-4 text-lg text-[var(--clv-text-soft)]">Your practice defines the rules. Clinova360 applies them consistently — deterministic, explainable and configurable.</p>
    </Reveal>
    <div className="mt-12 grid lg:grid-cols-[1fr_auto_1fr_auto_1fr] gap-6 items-stretch">
      {[
        {icon:Settings,h:"Your practice",items:["Staff","Working patterns","Leave","Rooms","Supervision","Special clinics","Capacity targets","Local rules","Google reviews","QOF exports"]},
        {icon:Cpu,h:"Clinova360 engine",items:["Deterministic rules","Capacity calculation","Constraint checking","Conflict detection","Operational planning","Online review analysis","QOF progress","Shift swaps","Staff cover suggestions"],mid:true},
        {icon:ClipboardCheck,h:"Operational plan",items:["Rota","Room plan","Capacity","Alerts","Recommendations","Patient sentiment","Suggested annual leave dates","Practice health review"]},
      ].map((c,idx)=>(
        <>
          <Reveal i={idx} key={c.h} className={`rounded-2xl border p-6 ${c.mid?"bg-[var(--clv-forest)] border-[var(--clv-forest)]":"bg-[var(--clv-card)] border-[var(--clv-border)]"}`}>
            <div className="flex items-center gap-2 mb-4"><c.icon size={20} style={{color:c.mid?"var(--clv-sage)":"var(--clv-primary)"}}/><h3 className={`text-xs font-bold tracking-widest uppercase ${c.mid?"text-[var(--clv-sage)]":"text-[var(--clv-primary)]"}`}>{c.h}</h3></div>
            <ul className="flex flex-col gap-2">{c.items.map(it=><li key={it} className={`text-sm flex items-center gap-2 ${c.mid?"text-[#dce7d8]":"text-[var(--clv-forest)]"}`}><span className="h-1.5 w-1.5 rounded-full" style={{background:"var(--clv-primary)"}}/>{it}</li>)}</ul>
          </Reveal>
          {idx<2 && <div key={`a${idx}`} className="hidden lg:flex items-center justify-center"><ArrowRight size={22} style={{color:"var(--clv-primary)"}}/></div>}
        </>
      ))}
    </div>
  </div></section>
);

const Capacity = () => (
  <section id="capacity" className="py-24 sm:py-28"><div className="max-w-6xl mx-auto px-6">
    <Reveal className="text-center max-w-2xl mx-auto">
      <p className="clv-eyebrow mb-3">Capacity & demand · forecasting</p>
      <h2 className="font-display text-3xl sm:text-4xl font-medium text-[var(--clv-forest)]">Know exactly what your rota delivers.</h2>
      <p className="mt-4 text-lg text-[var(--clv-text-soft)]">Practice capacity forecasting in appointments — not vague staffing estimates — at day and session level, so you can see future pressure before the rota is operational.</p>
    </Reveal>
    <div className="mt-12 grid lg:grid-cols-2 gap-6 items-start">
      <Reveal>
        <div className="rounded-2xl border border-[var(--clv-border)] bg-[var(--clv-card)] p-6">
          <div className="flex items-center justify-between mb-4"><span className="font-semibold text-[var(--clv-forest)]">Thursday 24 September</span><span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#fbeaea] text-[#c0453f]">● Capacity shortfall</span></div>
          <div className="grid grid-cols-3 gap-3"><Metric k="Demand / target" v="184"/><Metric k="Planned" v="168"/><Metric k="Shortfall" v="−16" red/></div>
        </div>
      </Reveal>
      <Reveal i={1}>
        <div className="rounded-2xl border border-[var(--clv-border)] bg-white p-6">
          <p className="clv-eyebrow" style={{color:"var(--clv-text-soft)"}}>Why is Thursday red?</p>
          <div className="mt-3 text-sm">
            {[["Target","184"],["Available capacity","168"],["Difference","−16","r"],["Clinician leave","−16","r"],["Additional session","+12","g"],["Other session changes","−12","r"]].map(([a,b,c])=>(
              <div key={a} className="flex justify-between py-2.5 border-b border-dashed border-[var(--clv-border)]"><span className="text-[var(--clv-text-soft)]">{a}</span><span className={c==="r"?"text-[#c0453f] font-semibold":c==="g"?"text-[#4f8a55] font-semibold":"text-[var(--clv-forest)]"}>{b}</span></div>
            ))}
            <div className="flex justify-between py-2.5 font-bold text-[var(--clv-forest)]"><span>Net effect</span><span className="text-[#c0453f]">−16</span></div>
          </div>
        </div>
      </Reveal>
    </div>
  </div></section>
);

const Leave = () => (
  <section className="py-24 sm:py-28 bg-[var(--clv-paper-2)]/60 border-y border-[var(--clv-border)]"><div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
    <Reveal>
      <p className="clv-eyebrow mb-3">Suggested annual leave</p>
      <h2 className="font-display text-3xl sm:text-4xl font-medium text-[var(--clv-forest)] leading-tight">Understand the operational effect of leave.</h2>
      <p className="mt-5 text-lg text-[var(--clv-text-soft)]">Leave isn't isolated from the rota. Clinova360 considers requests alongside future rota capacity and workforce availability — and can suggest annual leave periods that protect appointment capacity.</p>
    </Reveal>
    <Reveal i={1}>
      <div className="rounded-2xl border border-[var(--clv-border)] bg-[var(--clv-card)] p-6">
        <span className="font-semibold text-[var(--clv-forest)]">Suggested leave windows</span>
        <div className="mt-4 flex flex-col gap-3">
          {[["Week of 6 Oct","Low forecast demand · capacity protected","g"],["Week of 20 Oct","Balanced · cover available","g"],["Week of 24 Sep","Avoid · forecast shortfall −16","r"]].map(([w,d,c])=>(
            <div key={w} className="flex items-start gap-3 rounded-xl border border-[var(--clv-border)] bg-white px-4 py-3">
              <span className="h-2.5 w-2.5 rounded-full mt-1.5" style={{background:c==="g"?"#4f8a55":"#c0453f"}}/>
              <div><div className="font-medium text-[var(--clv-forest)] text-sm">{w}</div><div className="text-[13px] text-[var(--clv-text-soft)]">{d}</div></div>
            </div>
          ))}
        </div>
      </div>
    </Reveal>
  </div></section>
);

const PILLARS = [
  { tag:"PLAN", h:"Build the plan", items:["Rota generation","Working patterns","Room allocation","Supervision","Special clinics"] },
  { tag:"UNDERSTAND", h:"See the picture", items:["Capacity vs demand","Appointment-level capacity","Practice capacity forecasting","Leave impact"] },
  { tag:"ACT", h:"Take action", items:["Operational alerts","Recommended actions","Suggested annual leave","Capacity interventions"] },
  { tag:"RUN", h:"Run day-to-day", items:["Reception operations","Staff communication","Practice knowledge","Building checks"] },
];
const Platform = () => (
  <section id="platform" className="py-24 sm:py-28"><div className="max-w-6xl mx-auto px-6">
    <Reveal className="text-center max-w-2xl mx-auto"><p className="clv-eyebrow mb-3">The platform</p><h2 className="font-display text-3xl sm:text-4xl font-medium text-[var(--clv-forest)]">Plan. Understand. Act. Run.</h2></Reveal>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {PILLARS.map((p,i)=>(
        <Reveal i={i} key={p.tag}>
          <div className="h-full rounded-2xl border border-[var(--clv-border)] bg-[var(--clv-card)] p-6" style={{boxShadow:"0 20px 50px -38px rgba(26,51,34,.4)"}}>
            <span className="inline-block text-[11px] font-bold tracking-widest px-3 py-1.5 rounded-full mb-4" style={{background:"rgba(94,115,91,.12)",color:"var(--clv-primary-hover)"}}>{p.tag}</span>
            <h3 className="font-display text-lg font-semibold text-[var(--clv-forest)]">{p.h}</h3>
            <ul className="mt-3 flex flex-col gap-2">{p.items.map(it=><li key={it} className="text-[13.5px] text-[var(--clv-text-soft)]">{it}</li>)}</ul>
          </div>
        </Reveal>
      ))}
    </div>
  </div></section>
);

const PatientExperience = () => (
  <section className="py-24 sm:py-28 bg-[var(--clv-paper-2)]/60 border-y border-[var(--clv-border)]"><div className="max-w-4xl mx-auto px-6 text-center">
    <Reveal>
      <HeartPulse size={30} style={{color:"var(--clv-primary)"}} className="mx-auto mb-4"/>
      <p className="clv-eyebrow mb-3">Patient Experience Centre</p>
      <h2 className="font-display text-3xl sm:text-4xl font-medium text-[var(--clv-forest)]">Patient experience, in one place.</h2>
      <p className="mt-4 text-lg text-[var(--clv-text-soft)] max-w-2xl mx-auto">A dedicated space to capture and understand patient feedback and support practical improvement, connected to the way your practice operates.</p>
    </Reveal>
  </div></section>
);

const Security = () => (
  <section id="security" className="py-24 sm:py-28"><div className="max-w-6xl mx-auto px-6">
    <Reveal className="text-center max-w-2xl mx-auto"><p className="clv-eyebrow mb-3">Security & data</p><h2 className="font-display text-3xl sm:text-4xl font-medium text-[var(--clv-forest)]">Built for the information-governance needs of UK General Practice.</h2></Reveal>
    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {[
        [ShieldCheck,"UK hosted","Hosting and data residency in the UK."],
        [ShieldCheck,"UK GDPR-aligned","Designed around UK GDPR principles."],
        [ShieldCheck,"Role-based access","Access configured by role; secure authentication with MFA."],
        [ShieldCheck,"Encrypted & backed up","Encryption in transit and at rest, with backups, audit logging and data isolation."],
      ].map(([Icon,h,d],i)=>(
        <Reveal i={i} key={h}><div className="h-full rounded-2xl border border-[var(--clv-border)] bg-[var(--clv-card)] p-6">
          <Icon size={22} style={{color:"var(--clv-primary)"}}/><h3 className="font-display text-lg font-medium text-[var(--clv-forest)] mt-4 mb-2">{h}</h3><p className="text-[13.5px] text-[var(--clv-text-soft)]">{d}</p>
        </div></Reveal>
      ))}
    </div>
    <Reveal className="mt-6"><p className="text-sm text-[var(--clv-text-soft)] text-center">SaaS insured. Security architecture designed for the requirements of UK primary care.</p></Reveal>
  </div></section>
);

const About = () => (
  <section id="about" className="py-24 sm:py-28 bg-[var(--clv-paper-2)]/60 border-y border-[var(--clv-border)]"><div className="max-w-3xl mx-auto px-6 text-center">
    <Reveal>
      <p className="clv-eyebrow mb-3">About</p>
      <h2 className="font-display text-3xl sm:text-4xl font-medium text-[var(--clv-forest)]">Designed by a GP.</h2>
      <p className="mt-5 text-lg text-[var(--clv-text-soft)]">Clinova360 started with a problem inside General Practice: rota planning is never isolated. Rotas affect leave, leave affects capacity, capacity affects appointments, supervision affects staffing and rooms constrain delivery. The information existed in different places — Clinova360 was built to connect it, and is developed in collaboration with General Practice.</p>
    </Reveal>
  </div></section>
);

const Contact = () => {
  const [form, setForm] = useState({ name:"", email:"", practice:"", phone:"", message:"" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const u = k => e => setForm(f=>({...f,[k]:e.target.value}));
  const submit = async e => {
    e.preventDefault();
    if(!form.name.trim()||!form.email.trim()){toast.error("Please add your name and email.");return;}
    setLoading(true);
    try{
      await axios.post(`${API}/enquiries`,{name:form.name.trim(),email:form.email.trim(),practice:form.practice.trim()||null,phone:form.phone.trim()||null,message:form.message.trim()||null});
      setDone(true);toast.success("Thank you — we'll be in touch.");setForm({name:"",email:"",practice:"",phone:"",message:""});
    }catch{toast.error("Something went wrong. Please try again.");}finally{setLoading(false);}
  };
  const fld = `w-full rounded-xl border border-[var(--clv-border)] bg-white px-4 py-3 text-[var(--clv-forest)] placeholder:text-[#98a394] ${ring}`;
  return (
    <section id="contact" className="py-24 sm:py-28"><div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
      <div>
        <p className="clv-eyebrow mb-3">Request a demo</p>
        <h2 className="font-display text-3xl sm:text-5xl font-light text-[var(--clv-forest)] leading-[1.1]">See Clinova360 with your practice's rules.</h2>
        <p className="mt-5 text-lg text-[var(--clv-text-soft)] max-w-lg">A short, no-obligation demonstration for practice managers, GP partners and practice teams.</p>
        <a href="mailto:hello@clinova360.co.uk" className="mt-6 inline-flex items-center gap-3 text-[var(--clv-forest)]"><span className="h-10 w-10 rounded-lg flex items-center justify-center" style={{background:"rgba(94,115,91,.14)"}}><Mail size={20} style={{color:"var(--clv-primary)"}}/></span>hello@clinova360.co.uk</a>
      </div>
      <div className="bg-[var(--clv-card)] rounded-3xl border border-[var(--clv-border)] shadow-xl p-7 sm:p-9">
        {done ? (
          <div data-testid="contact-success" className="text-center py-8 flex flex-col items-center">
            <div className="h-16 w-16 rounded-full flex items-center justify-center mb-5" style={{background:"rgba(94,115,91,.14)"}}><CheckCircle2 size={32} style={{color:"var(--clv-primary)"}}/></div>
            <h3 className="font-display text-2xl font-medium text-[var(--clv-forest)]">Thank you</h3>
            <p className="mt-3 text-[var(--clv-text-soft)]">We've received your enquiry and will be in touch to arrange a demonstration.</p>
            <button data-testid="contact-reset-button" onClick={()=>setDone(false)} className={`mt-7 rounded-full px-6 py-3 text-sm font-medium text-[var(--clv-forest)] border border-[var(--clv-border)] ${ring}`}>Send another enquiry</button>
          </div>
        ) : (
          <form data-testid="contact-form" onSubmit={submit} className="space-y-5" noValidate>
            <div className="grid sm:grid-cols-2 gap-5">
              <div><label htmlFor="n" className="block text-sm font-medium text-[var(--clv-forest)] mb-2">Full name*</label><input id="n" data-testid="contact-name-input" value={form.name} onChange={u("name")} placeholder="Jane Smith" className={fld}/></div>
              <div><label htmlFor="e" className="block text-sm font-medium text-[var(--clv-forest)] mb-2">Work email*</label><input id="e" type="email" data-testid="contact-email-input" value={form.email} onChange={u("email")} placeholder="jane@practice.nhs.uk" className={fld}/></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div><label htmlFor="o" className="block text-sm font-medium text-[var(--clv-forest)] mb-2">Practice</label><input id="o" data-testid="contact-practice-input" value={form.practice} onChange={u("practice")} placeholder="Your practice" className={fld}/></div>
              <div><label htmlFor="p" className="block text-sm font-medium text-[var(--clv-forest)] mb-2">Phone</label><input id="p" data-testid="contact-phone-input" value={form.phone} onChange={u("phone")} placeholder="Optional" className={fld}/></div>
            </div>
            <div><label htmlFor="m" className="block text-sm font-medium text-[var(--clv-forest)] mb-2">How can we help?</label><textarea id="m" data-testid="contact-message-input" value={form.message} onChange={u("message")} rows={4} placeholder="A little about your practice." className={`${fld} resize-none`}/></div>
            <button data-testid="contact-submit-button" type="submit" disabled={loading} className={`w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-medium text-white disabled:opacity-60 ${ring}`} style={{background:"var(--clv-primary)"}}>{loading?<><Loader2 size={17} className="animate-spin"/> Sending…</>:<>Request a demo <ArrowUpRight size={17}/></>}</button>
            <p className="text-xs text-[var(--clv-text-soft)] text-center">We'll only use your details to respond to this enquiry.</p>
          </form>
        )}
      </div>
    </div></section>
  );
};

const Footer = () => (
  <footer className="pt-14 pb-9" style={{background:"var(--clv-forest)"}}><div className="max-w-6xl mx-auto px-6">
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-8 border-b border-white/10">
      <div className="flex items-center gap-2"><img src={SYMBOL} alt="" className="h-8 w-8 object-contain bg-white rounded-lg p-1"/><span className="font-display text-xl font-semibold text-white">Clinova<span style={{color:"var(--clv-sage)"}}>360</span></span></div>
      <nav aria-label="Footer" className="flex flex-wrap gap-x-7 gap-y-2">{NAV.map(l=><a key={l.href} href={l.href} className="text-[#cdd8c7] hover:text-white text-sm">{l.label}</a>)}</nav>
    </div>
    <div className="pt-7 flex flex-col sm:flex-row justify-between gap-3 text-[#9caf93] text-sm">
      <span>© {new Date().getFullYear()} Clinova360 · Operational intelligence for General Practice</span>
      <span>Designed by a GP · UK hosted · hello@clinova360.co.uk</span>
    </div>
  </div></footer>
);

export default function Landing() {
  return (
    <div data-testid="landing-page" className="bg-transparent text-[var(--clv-forest)]">
      <Toaster position="top-center" richColors />
      <Nav />
      <main>
        <Hero /><Engine /><Connected /><Capacity /><Leave /><Platform /><PatientExperience /><Security /><About /><Contact />
      </main>
      <Footer />
    </div>
  );
}
